import { useEffect, useState } from "react";
import { Loader2, Radar, Sparkles, RefreshCw, AlertTriangle, CheckCircle2, TrendingUp, ExternalLink, Copy, Zap, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import AppHeader from "../components/AppHeader";

export default function AdminSEO() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [radar, setRadar] = useState(null);
  const [radarLoading, setRadarLoading] = useState(false);
  const [radarError, setRadarError] = useState("");
  const [pages, setPages] = useState(null);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [suggestError, setSuggestError] = useState("");
  const [days, setDays] = useState(28);
  const [minImpr, setMinImpr] = useState(5);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [autoGenResult, setAutoGenResult] = useState(null);

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get("/admin/seo/status");
      setStatus(data);
    } catch (e) {
      setStatus({ configured: false, error: formatApiError(e?.response?.data?.detail) });
    } finally {
      setStatusLoading(false);
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const runRadar = async () => {
    setRadarLoading(true);
    setRadarError("");
    try {
      const { data } = await api.get(`/admin/seo/keyword-radar?days=${days}&min_impressions=${minImpr}`);
      setRadar(data);
    } catch (e) {
      setRadarError(formatApiError(e?.response?.data?.detail) || "Error consultando GSC");
    } finally {
      setRadarLoading(false);
    }
  };

  const runPages = async () => {
    setPagesLoading(true);
    try {
      const { data } = await api.get(`/admin/seo/underperforming-pages?days=${days}`);
      setPages(data);
    } catch (e) {
      setPages({ error: formatApiError(e?.response?.data?.detail) });
    } finally {
      setPagesLoading(false);
    }
  };

  const suggestArticles = async (saveToPlan) => {
    setSuggesting(true);
    setSuggestError("");
    setSuggested(null);
    try {
      const { data } = await api.post("/admin/seo/suggest-articles", {
        days,
        max_queries: 15,
        save_to_plan: saveToPlan,
      });
      setSuggested(data);
    } catch (e) {
      setSuggestError(formatApiError(e?.response?.data?.detail) || "Error generando sugerencias");
    } finally {
      setSuggesting(false);
    }
  };

  const autoGenerateNow = async () => {
    if (!window.confirm(
      "¿Escribir AHORA un borrador completo?\n\n" +
      "Coge el siguiente artículo pendiente de la cola (prioridad: Keyword Radar → IA → hardcoded),\n" +
      "y genera contenido SEO + imagen de portada (~30-60 s).\n\n" +
      "Aparecerá como BORRADOR en /admin/blog."
    )) return;
    setAutoGenerating(true);
    setAutoGenResult(null);
    try {
      const { data } = await api.post("/admin/blog/auto-generate-now");
      setAutoGenResult({ ok: true, ...data });
    } catch (e) {
      setAutoGenResult({ ok: false, error: formatApiError(e?.response?.data?.detail) || "Error generando" });
    } finally {
      setAutoGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100" data-testid="admin-seo-page">
      <AppHeader />
      <header className="px-6 py-5 bg-stone-900 border-b border-stone-800">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F]">Admin · SEO</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white flex items-center gap-3">
            <Radar className="w-7 h-7 text-[#E0651F]" /> Keyword Radar
          </h1>
          <div className="text-sm text-stone-400 mt-1">
            Google Search Console → oportunidades pos. 11-30 → Claude sugiere artículos.
          </div>
          <div className="mt-3 text-xs text-stone-500 bg-stone-800/40 border border-stone-800 rounded-sm p-3">
            <b className="text-stone-300">Cómo funciona el flujo completo:</b>
            <ol className="mt-1 space-y-0.5 list-decimal ml-4">
              <li><b>Escanear Keywords</b> → GSC muestra qué queries están a 1 empujón del Top 10.</li>
              <li><b>Sugerir 5 artículos</b> → Claude propone 5 títulos (solo preview, no se guarda).</li>
              <li><b>Añadir a cola blog</b> → guarda los TÍTULOS en la cola. Los artículos completos se escriben después.</li>
              <li><b>Escribir borrador ahora</b> → coge el siguiente de la cola y genera contenido + imagen (~30-60 s).</li>
            </ol>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8 space-y-6">

        {/* STATUS CARD */}
        <div className="bg-stone-900 border border-stone-800 rounded-sm p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-3">Estado de la integración</div>
          {statusLoading ? (
            <div className="flex items-center gap-2 text-stone-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Comprobando…</div>
          ) : !status?.configured ? (
            <div className="flex items-start gap-3 text-red-400">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="text-sm">
                Service Account no configurada. Añade <code className="bg-stone-800 px-1">GSC_SERVICE_ACCOUNT_JSON</code> al `.env` del backend.
              </div>
            </div>
          ) : status?.gsc_reachable ? (
            <div className="flex items-start gap-3 text-emerald-400">
              <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-emerald-300">Conectado ✓</div>
                <div className="text-xs text-stone-400 mt-1">
                  Site: <code className="text-stone-300">{status.site_url}</code><br />
                  Cuenta: <code className="text-stone-300">{status.service_account_email}</code>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 text-amber-400">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold">Autenticada pero sin permisos en GSC</div>
                <div className="text-xs text-stone-400 mt-1">{status.error}</div>
                {status.hint && <div className="text-xs text-stone-300 mt-2">💡 {status.hint}</div>}
                <div className="text-xs text-stone-400 mt-2">
                  Añade <code className="text-stone-300">{status.service_account_email}</code> con permiso <b>Completo</b> en Search Console.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CONTROLS */}
        <div className="bg-stone-900 border border-stone-800 rounded-sm p-5 flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 block mb-1">Días</label>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="bg-stone-800 border border-stone-700 text-sm px-3 py-2 rounded-sm text-stone-100" data-testid="seo-days-select">
              <option value={7}>7 días</option>
              <option value={28}>28 días</option>
              <option value={90}>90 días</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 block mb-1">Min. impresiones</label>
            <input type="number" min={1} value={minImpr} onChange={(e) => setMinImpr(Number(e.target.value))} className="bg-stone-800 border border-stone-700 text-sm px-3 py-2 rounded-sm text-stone-100 w-24" data-testid="seo-min-impr" />
          </div>
          <button
            onClick={runRadar}
            disabled={radarLoading || !status?.gsc_reachable}
            className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm"
            data-testid="seo-run-radar-btn"
          >
            {radarLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />}
            Escanear Keywords
          </button>
          <button
            onClick={runPages}
            disabled={pagesLoading || !status?.gsc_reachable}
            className="inline-flex items-center gap-1.5 border border-stone-700 hover:bg-stone-800 disabled:opacity-40 text-stone-100 px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm"
            data-testid="seo-run-pages-btn"
          >
            {pagesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
            Páginas con bajo CTR
          </button>
          {radar?.opportunities?.length > 0 && (
            <>
              <button
                onClick={() => suggestArticles(false)}
                disabled={suggesting}
                className="inline-flex items-center gap-1.5 bg-purple-700 hover:bg-purple-600 disabled:opacity-40 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm ml-auto"
                data-testid="seo-suggest-preview-btn"
              >
                {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Sugerir 5 artículos (preview)
              </button>
              <button
                onClick={() => suggestArticles(true)}
                disabled={suggesting}
                className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm"
                data-testid="seo-suggest-save-btn"
                title="Genera 5 títulos con Claude y los añade a la cola. Después escribe los borradores con 'Escribir borrador ahora'."
              >
                {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Añadir 5 títulos a cola blog
              </button>
            </>
          )}
        </div>

        {/* RADAR RESULTS */}
        {radarError && (
          <div className="bg-red-950/40 border border-red-900 text-red-300 p-4 rounded-sm text-sm">{radarError}</div>
        )}
        {radar && (
          <div className="bg-stone-900 border border-stone-800 rounded-sm overflow-hidden" data-testid="seo-radar-results">
            <div className="px-5 py-3 border-b border-stone-800 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Radar de keywords</div>
                <div className="text-sm text-stone-300">
                  <b className="text-white">{radar.opportunity_count}</b> oportunidades · analizadas {radar.total_queries_analyzed} queries · ventana {radar.days}d
                </div>
              </div>
              <button onClick={runRadar} className="text-stone-500 hover:text-stone-300" title="Refrescar">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-stone-800/50 text-stone-400 text-[10px] uppercase tracking-[0.15em]">
                <tr>
                  <th className="text-left px-4 py-2">Query</th>
                  <th className="text-right px-4 py-2">Pos.</th>
                  <th className="text-right px-4 py-2">Impr.</th>
                  <th className="text-right px-4 py-2">Clicks</th>
                  <th className="text-right px-4 py-2">CTR</th>
                  <th className="text-right px-4 py-2">Potencial clicks/mes</th>
                  <th className="text-left px-4 py-2">Página que rankea</th>
                </tr>
              </thead>
              <tbody>
                {radar.opportunities?.map((o, i) => (
                  <tr key={i} className="border-t border-stone-800/70 hover:bg-stone-800/30">
                    <td className="px-4 py-2 text-stone-100 font-medium">{o.query}</td>
                    <td className="px-4 py-2 text-right text-stone-300">{o.position}</td>
                    <td className="px-4 py-2 text-right text-stone-300">{o.impressions}</td>
                    <td className="px-4 py-2 text-right text-stone-400">{o.clicks}</td>
                    <td className="px-4 py-2 text-right text-stone-400">{(o.ctr * 100).toFixed(1)}%</td>
                    <td className="px-4 py-2 text-right text-emerald-400 font-bold">+{o.opportunity_clicks}</td>
                    <td className="px-4 py-2 text-xs text-stone-400 max-w-[280px] truncate">
                      {o.top_page ? (
                        <a href={o.top_page} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#E0651F]">
                          {o.top_page.replace(/^https?:\/\/[^/]+/, "")} <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* SUGGEST ARTICLES OUTPUT */}
        {suggestError && (
          <div className="bg-red-950/40 border border-red-900 text-red-300 p-4 rounded-sm text-sm">{suggestError}</div>
        )}
        {suggested && (
          <div className="bg-purple-950/20 border border-purple-800/60 rounded-sm p-5" data-testid="seo-suggested">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-purple-300">Artículos sugeridos por Claude</div>
                <div className="text-sm text-stone-300">
                  {suggested.articles_generated} artículo(s) · {suggested.articles_saved_to_plan > 0 ? `${suggested.articles_saved_to_plan} añadidos a la cola del blog` : "modo preview (no guardados)"}
                </div>
              </div>
            </div>

            {suggested.articles_saved_to_plan > 0 && (
              <div className="bg-emerald-950/40 border border-emerald-800/60 rounded-sm p-4 mb-4" data-testid="seo-next-steps">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-stone-200 flex-1">
                    <div className="font-bold text-emerald-300 mb-1">✓ {suggested.articles_saved_to_plan} títulos añadidos a la cola del blog.</div>
                    <div className="text-stone-300 text-xs leading-relaxed">
                      <b>¿Qué pasa ahora?</b> Solo hemos guardado los TÍTULOS, no los artículos completos.
                      Para que se conviertan en borradores publicables tienes 2 opciones:
                      <ol className="mt-2 space-y-1 list-decimal ml-4">
                        <li>
                          <b>Automático:</b> el auto-scheduler los escribirá uno a uno (siguiente ejecución programada).
                        </li>
                        <li>
                          <b>Manual (ahora):</b> pulsa el botón <b>&quot;Escribir borrador ahora&quot;</b> aquí abajo para generar el próximo pendiente en 30-60 s.
                        </li>
                        <li>
                          <b>O ve al Editor:</b> <Link to="/admin/blog" className="text-[#E0651F] underline">/admin/blog</Link> → &quot;Nuevo artículo&quot; → los verás en el desplegable con la etiqueta <code className="bg-stone-800 px-1 rounded">🎯 [GSC Radar]</code>.
                        </li>
                      </ol>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      <button
                        onClick={autoGenerateNow}
                        disabled={autoGenerating}
                        className="inline-flex items-center gap-1.5 bg-amber-700 hover:bg-amber-600 disabled:opacity-40 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm"
                        data-testid="seo-auto-generate-now-btn"
                      >
                        {autoGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        Escribir borrador ahora
                      </button>
                      <Link
                        to="/admin/blog"
                        className="inline-flex items-center gap-1.5 border border-stone-700 hover:bg-stone-800 text-stone-100 px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm"
                        data-testid="seo-goto-blog-btn"
                      >
                        Ir al editor de blog <ArrowRight className="w-4 h-4" />
                      </Link>
                      {autoGenResult && (
                        autoGenResult.ok ? (
                          <span className="text-emerald-400 text-xs inline-flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4" /> Borrador creado ✓ {autoGenResult.slug && <Link to={`/admin/blog`} className="underline">Ver</Link>}
                          </span>
                        ) : (
                          <span className="text-red-400 text-xs inline-flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" /> {autoGenResult.error}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3 mt-4">
              {suggested.articles?.map((a, i) => (
                <div key={i} className="bg-stone-900/70 border border-stone-800 rounded-sm p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-bold text-white">{a.title}</div>
                      <div className="text-xs text-stone-500 mt-1">
                        <span className="bg-stone-800 px-2 py-0.5 rounded-sm">{a.category}</span>
                        {a.keywords?.length > 0 && (
                          <span className="ml-2">kw: {a.keywords.join(", ")}</span>
                        )}
                      </div>
                      <div className="text-sm text-stone-400 mt-2">{a.notes}</div>
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(a.title)}
                      className="text-stone-500 hover:text-stone-300 shrink-0"
                      title="Copiar título"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* UNDERPERFORMING PAGES */}
        {pages && !pages.error && (
          <div className="bg-stone-900 border border-stone-800 rounded-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-stone-800">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Páginas con alto tráfico y bajo CTR</div>
              <div className="text-sm text-stone-300">{pages.pages?.length || 0} candidatas a reescribir title/meta description</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-stone-800/50 text-stone-400 text-[10px] uppercase tracking-[0.15em]">
                <tr>
                  <th className="text-left px-4 py-2">Página</th>
                  <th className="text-right px-4 py-2">Impr.</th>
                  <th className="text-right px-4 py-2">Clicks</th>
                  <th className="text-right px-4 py-2">CTR</th>
                  <th className="text-right px-4 py-2">Pos.</th>
                </tr>
              </thead>
              <tbody>
                {pages.pages?.map((p, i) => (
                  <tr key={i} className="border-t border-stone-800/70">
                    <td className="px-4 py-2 max-w-[420px] truncate">
                      <a href={p.page} target="_blank" rel="noreferrer" className="text-stone-100 hover:text-[#E0651F] inline-flex items-center gap-1">
                        {p.page.replace(/^https?:\/\/[^/]+/, "")} <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="px-4 py-2 text-right text-stone-300">{p.impressions}</td>
                    <td className="px-4 py-2 text-right text-stone-400">{p.clicks}</td>
                    <td className="px-4 py-2 text-right text-amber-400">{(p.ctr * 100).toFixed(2)}%</td>
                    <td className="px-4 py-2 text-right text-stone-400">{p.position}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
